import { useCallback, useEffect, useState } from 'react';
import { FilePen, PencilLine } from 'lucide-react';
import { formatCurrency } from '@/lib/format.js';
import Panel from './Panel.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import LogGlQuoteSheet from '@/components/sheets/LogGlQuoteSheet.jsx';
import QuoteSourceDocs from './QuoteSourceDocs.jsx';
import { getEnvelopeQuotes, getQuoteFull } from '@/services/quotes.service.js';

const COVERAGE_ROWS = [
  { key: 'premises', label: 'Premises Liability' },
  { key: 'medical_expense', label: 'Medical Payments', singleLimit: 'each_person' },
  { key: 'fire_legal_liability', label: 'Damage to Premises Rented', singleLimit: 'each_occurrence' },
  { key: 'personal_injury', label: 'Personal & Advertising Injury' },
  { key: 'products_completed_ops', label: 'Products & Completed Ops' },
  { key: 'hkll', label: 'Hangarkeepers', limitLabels: { each_aircraft: 'Per Aircraft', each_occurrence: 'Per Occurrence' } },
];

function LimitCell({ limits, row }) {
  if (!limits) return <span className="text-text-dim">—</span>;
  if (row.singleLimit) {
    const v = limits[row.singleLimit];
    return <span>{v ? formatCurrency(v) : '—'}</span>;
  }
  if (row.limitLabels) {
    const parts = Object.entries(row.limitLabels)
      .map(([k, label]) => limits[k] ? `${formatCurrency(limits[k])} ${label}` : null)
      .filter(Boolean);
    return parts.length > 0
      ? <span>{parts.join(' / ')}</span>
      : <span className="text-text-dim">—</span>;
  }
  const occ = limits.each_occurrence;
  const per = limits.each_person;
  if (occ && per) return <span>{formatCurrency(occ)} / {formatCurrency(per)}</span>;
  if (occ) return <span>{formatCurrency(occ)}</span>;
  if (per) return <span>{formatCurrency(per)}</span>;
  return <span className="text-text-dim">—</span>;
}

function hasAnyCoverage(coverage) {
  if (!coverage?.limits) return false;
  return Object.values(coverage.limits).some(
    (v) => v && typeof v === 'object' && Object.values(v).some(Boolean)
  );
}

export default function CommercialCoveragePanel({ dealId, manifest, loading }) {
  const [quoteOpen, setQuoteOpen] = useState(false);
  // Edit-in-place: the full quote (with lines) being edited, or null = new.
  const [editQuote, setEditQuote] = useState(null);
  // Logged envelope quotes on this deal (visible or hidden), the Edit door's list.
  const [quotes, setQuotes] = useState([]);
  const loadQuotes = useCallback(() => {
    if (!dealId) return;
    getEnvelopeQuotes(dealId)
      .then((r) => setQuotes(Array.isArray(r?.quotes) ? r.quotes : []))
      .catch(() => { /* the list is a convenience; a failed read leaves the last one */ });
  }, [dealId]);
  useEffect(() => { loadQuotes(); }, [loadQuotes]);
  const coverage = manifest?.coverage;
  const isEmpty = !coverage || !hasAnyCoverage(coverage);
  const limits = coverage?.limits;
  const general_aggregate = coverage?.general_aggregate;

  // §7 Slice 4a — broker "Log GL quote" entry point. Always available (even
  // before manifest coverage is entered) so the broker can log a carrier quote.
  const quoteButton = dealId ? (
    <div className="mb-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-text-dim">Quotes</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setEditQuote(null); setQuoteOpen(true); }}
          title="Enter a carrier's commercial GL quote — presents in the client's Tower and is bindable"
        >
          <FilePen className="mr-1 h-3.5 w-3.5" />
          Log GL quote
        </Button>
      </div>
      {quotes.length > 0 ? (
        <div className="mb-2 space-y-1">
          {quotes.map((q) => (
            <div key={q.id} className="flex items-center justify-between gap-2 rounded-card border border-border px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm text-text">{q.carrier}</p>
                <p className="text-[11px] text-text-dim">
                  {[
                    q.total_premium != null ? `$${Number(q.total_premium).toLocaleString('en-US')} base` : null,
                    q.effective_date || null,
                    q.status,
                    q.client_visible ? 'client-visible' : 'hidden from client',
                  ].filter(Boolean).join(' · ')}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={['declined', 'expired', 'bound', 'superseded'].includes(q.status)}
                title="Edit this quote in place (limits, premiums, optional flags)"
                onClick={async () => {
                  try {
                    const res = await getQuoteFull(q.id);
                    if (!res?.success) { toast.error(res?.error || 'Could not load the quote'); return; }
                    setEditQuote(res.quote);
                    setQuoteOpen(true);
                  } catch (e) { toast.error(e?.body?.error || e?.message || 'Could not load the quote'); }
                }}
              >
                <PencilLine className="mr-1 h-3.5 w-3.5" />
                Edit
              </Button>
            </div>
          ))}
        </div>
      ) : null}
      <QuoteSourceDocs dealId={dealId} />
    </div>
  ) : null;

  const sheet = dealId ? (
    <LogGlQuoteSheet
      dealId={dealId}
      open={quoteOpen}
      onOpenChange={(o) => { setQuoteOpen(o); if (!o) setEditQuote(null); }}
      quote={editQuote}
      onSaved={loadQuotes}
    />
  ) : null;

  if (loading) {
    return <>{quoteButton}<Panel title="Coverage" empty="Loading…" />{sheet}</>;
  }
  if (isEmpty) {
    return <>{quoteButton}<Panel title="Coverage" empty="Coverage not yet entered — open Commercial Build to add, or Log GL quote above." />{sheet}</>;
  }

  return (
    <>
    {quoteButton}
    <Panel title="Coverage">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="pb-1.5 font-semibold text-text-muted">Line</th>
            <th className="pb-1.5 text-right font-semibold text-text-muted">Limit</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {COVERAGE_ROWS.map((row) => {
            const rowLimits = limits?.[row.key];
            if (!rowLimits || !Object.values(rowLimits).some(Boolean)) return null;
            return (
              <tr key={row.key}>
                <td className="py-1.5 text-text">{row.label}</td>
                <td className="py-1.5 text-right font-mono text-text">
                  <LimitCell limits={rowLimits} row={row} />
                </td>
              </tr>
            );
          })}
          {general_aggregate?.exists ? (
            <tr>
              <td className="py-1.5 text-text">General Aggregate</td>
              <td className="py-1.5 text-right font-mono text-text">
                {general_aggregate.amount ? formatCurrency(general_aggregate.amount) : '—'}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {/* HK base values from hangarkeepers section */}
      {manifest?.hangarkeepers?.base_values ? (
        <HkBaseValues values={manifest.hangarkeepers.base_values} />
      ) : null}

      {/* TRIPRA election */}
      {coverage.additional?.length > 0 ? (
        <div className="mt-2 text-[10px] text-text-dim">
          {coverage.additional.map((a, i) => (
            <span key={i} className="mr-2">{a.label || a.type}: {a.value || '—'}</span>
          ))}
        </div>
      ) : null}
    </Panel>
    {sheet}
    </>
  );
}

function HkBaseValues({ values }) {
  const rows = [
    { key: 'piston_fw', label: 'Piston FW' },
    { key: 'turbine_fw', label: 'Turbine FW' },
    { key: 'airline', label: 'Airline' },
    { key: 'rotorwing', label: 'Rotorwing' },
  ].filter((r) => values[r.key]?.highest || values[r.key]?.total);

  if (rows.length === 0) return null;

  return (
    <div className="mt-3">
      <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-text-dim">
        HK Base Values
      </h4>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="pb-1 font-medium text-text-muted">Type</th>
            <th className="pb-1 text-right font-medium text-text-muted">Highest</th>
            <th className="pb-1 text-right font-medium text-text-muted">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {rows.map((r) => (
            <tr key={r.key}>
              <td className="py-1 text-text">{r.label}</td>
              <td className="py-1 text-right font-mono text-text">
                {values[r.key].highest ? formatCurrency(values[r.key].highest) : '—'}
              </td>
              <td className="py-1 text-right font-mono text-text">
                {values[r.key].total ? formatCurrency(values[r.key].total) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
